import { bbox, featureCollection, point } from '@turf/turf';
import { type LngLatLike, Map as MapboxMap } from 'mapbox-gl';
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { HttpService } from '@/features/api/http.service';
import type { ILocation } from '@/types';
import {
  keptMapPitch,
  MAP_BASEMAP_CONFIG,
  quietRoadShields,
  STANDARD_STYLE_URL,
} from '../utils/basemap';
import { radiusToZoomLevel } from '../utils/radiusHelpers';

/**
 * Mapbox GL fetches tiles itself, bypassing axios, so it would not send the
 * session cookie our tile endpoint requires. Our API origin only: Mapbox's own
 * CDN would reject a credentialed cross-origin request.
 */
function transformRequest(url: string) {
  if (!HttpService.isApiRequestUrl(url)) return { url };
  return { url, credentials: 'include' as const };
}

// Core map initialization and lifecycle management
export function useMap(
  containerRef: RefObject<HTMLDivElement | null>,
  defaultCoordinates: LngLatLike,
  defaultZoom: number,
  projectId: string,
  projectLocations: ILocation[],
  currentLocation?: ILocation,
) {
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const map = useRef<MapboxMap | null>(null);
  const prevProject = useRef<string | null>(null);
  const isInitializing = useRef<boolean>(false);
  const allCoordinates = useMemo<[number, number][]>(
    () =>
      projectLocations.map((location) => [
        location.coordinates.longitude,
        location.coordinates.latitude,
      ]),
    [projectLocations],
  );

  function getFitBounds(coords: [number, number][]) {
    const fc = featureCollection(coords.map((c) => point(c)));
    const [minLng, minLat, maxLng, maxLat] = bbox(fc);
    return [
      [minLng, minLat],
      [maxLng, maxLat],
    ] as [[number, number], [number, number]];
  }

  function getInitialMapCenter() {
    const current = map.current;
    if (!current) return;
    const pitch = keptMapPitch(current);
    if (allCoordinates.length === 1) {
      current.flyTo({
        center: allCoordinates[0],
        zoom: radiusToZoomLevel(currentLocation?.radius ?? 0),
        pitch,
        essential: true,
      });
    } else if (allCoordinates.length > 1) {
      const bounds = getFitBounds(allCoordinates);
      current.fitBounds(bounds, {
        padding: { top: 100, bottom: 50, left: 50, right: 50 },
        duration: 1000,
        pitch,
      });
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: only on container setup
  useEffect(() => {
    // Wait for the container ref to be set
    if (!containerRef.current) return;

    // If project changed, tear down existing map
    if (prevProject.current !== projectId && map.current) {
      map.current.remove();
      map.current = null;
    }

    // Prevent duplicate init: if already have a map for this project or currently initializing, bail
    if ((prevProject.current === projectId && map.current) || isInitializing.current) return;

    prevProject.current = projectId;
    isInitializing.current = true;
    setIsMapLoaded(false);

    map.current = new MapboxMap({
      container: containerRef.current,
      style: STANDARD_STYLE_URL,
      config: MAP_BASEMAP_CONFIG,
      center: allCoordinates[0] ?? defaultCoordinates,
      zoom:
        allCoordinates.length > 0 ? radiusToZoomLevel(currentLocation?.radius ?? 0) : defaultZoom,
      antialias: true,
      transformRequest,
      // Map-wide: no layer property overrides the symbol fade Mapbox runs on a tile
      // swap or a zoom boundary, which is what makes the zone-cluster count pulse.
      fadeDuration: 0,
    });

    const onLoad = () => {
      if (map.current) quietRoadShields(map.current);
      setIsMapLoaded(true);
      isInitializing.current = false;
    };

    // Ensure the handler fires only once per map instance
    map.current.once('load', onLoad);

    const onMapReady = () => {
      // The basemap import can arrive after `load`. Applying twice paints the same thing.
      if (map.current) quietRoadShields(map.current);
      getInitialMapCenter();
    };

    map.current.once('idle', onMapReady);

    // clean map on unmount
    return () => {
      isInitializing.current = false;
      map?.current?.off('load', onLoad);
      map?.current?.off('idle', onMapReady);
      map?.current?.remove();
      map.current = null;
    };
  }, [projectId]); // React to changes in project ID

  return { map, isMapLoaded };
}
